import React, { useEffect, useMemo, useState } from 'react';
import { TreeSelect } from 'antd';
import { useTranslation } from 'react-i18next';
import type { DisplayValueType } from 'rc-select/lib/interface';
import { SiteFilterGlobalStyles } from './SiteFilter.styles';
import {
  getTriggerLabel,
  getSiteTreeData,
  toApiValue,
  toApiMachineIds,
  buildNoSiteMachineValue,
  SiteFilterSite,
  ALL_SITES_NODE_VALUE,
} from './SiteFilter.utils';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';

interface SiteFilterProps {
  sitesData: SiteFilterSite[];
  noSiteMachines?: SiteFilterSite[];
  onFilterChange?: (siteIds: string[] | undefined, machineIds: string[] | undefined) => void;
  isLoading?: boolean;
}

export const SiteFilter = ({
  sitesData,
  noSiteMachines = [],
  onFilterChange,
  isLoading = false,
}: SiteFilterProps): React.JSX.Element => {
  const { t } = useTranslation();
  const treeData = useMemo(() => getSiteTreeData(t, sitesData, noSiteMachines), [t, sitesData, noSiteMachines]);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  // Sites and no-site machines resolve from two independent requests. Once the user has made their
  // own selection, later data (e.g. no-site machines arriving after sites) must not overwrite it.
  const [hasUserChangedSelection, setHasUserChangedSelection] = useState(false);

  useEffect(() => {
    if (!hasUserChangedSelection && (sitesData.length > 0 || noSiteMachines.length > 0)) {
      setSelectedValues([...sitesData.map(s => s.id), ...noSiteMachines.map(m => buildNoSiteMachineValue(m.id))]);
    }
  }, [sitesData, noSiteMachines, hasUserChangedSelection]);

  const maxTagPlaceholder = (omittedValues: DisplayValueType[]): React.ReactNode => {
    const values = omittedValues.map(v => v.value as string);
    return <span className="site-filter__placeholder">{getTriggerLabel(values, t, sitesData)}</span>;
  };

  return (
    <>
      <SiteFilterGlobalStyles />
      <div className="site-filter-trigger">
        <TreeSelect
          className="site-filter"
          popupClassName="site-filter-dropdown"
          treeData={treeData}
          treeCheckable
          showCheckedStrategy={TreeSelect.SHOW_CHILD}
          value={selectedValues}
          onChange={(values: string[]): void => {
            const next = values ?? [];
            setSelectedValues(next);
            setHasUserChangedSelection(true);
            onFilterChange?.(toApiValue(next, sitesData), toApiMachineIds(next, noSiteMachines));
          }}
          onSearch={(value: string): void => setSearchValue(value)}
          onDropdownVisibleChange={(open): void => {
            setIsOpen(open);
            if (open) setSearchValue('');
          }}
          maxTagCount={0}
          maxTagPlaceholder={maxTagPlaceholder}
          showSearch
          searchValue={searchValue}
          filterTreeNode={(input, node): boolean => {
            if (node.value === ALL_SITES_NODE_VALUE) return false;
            return String(node.title).toLowerCase().includes(input.toLowerCase());
          }}
          treeDefaultExpandAll
          allowClear={false}
          popupMatchSelectWidth={false}
          loading={isLoading}
          suffixIcon={isLoading ? undefined : <SvgIcon name="chevronDown" className="site-filter__suffix-icon" />}
          switcherIcon={<SvgIcon name="chevronDown" className="site-filter__switcher-icon" />}
        />
        {selectedValues.length === 0 && !isOpen && (
          <span className="site-filter__placeholder site-filter__empty-label">
            {t('machineDashboard.siteFilter.sites', { count: 0 })}
          </span>
        )}
      </div>
    </>
  );
};
